import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Chip, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { type FC, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock, MainActionButton } from '@focuslee/shared-mini-app';
import { PurchaseRewardMutation, PurchaseRewardMutationVariables } from '../client/generated/graphql';
import { purchaseRewardMutation } from '../client/mutations/purchaseReward';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { toDueDate } from '../helpers/datetime';
import { getRewardColor } from '../helpers/reward';
import { useCachedCollectedCurrency, useCachedReward } from '../hooks/caching/reward';
import { useAnalytics } from '../hooks/useAnalytics';
import { rewardEditPath } from '../navigation/paths';

export const RewardViewPage: FC = () => {
    const navigate = useNavigate();
    const { id: rewardId } = useParams<{ id: string }>();
    const { recordEvent } = useAnalytics();
    const reward = useCachedReward(rewardId);
    const collectedCurrency = useCachedCollectedCurrency();

    const [purchaseReward, { loading: isPurchasing }] = useMutation<PurchaseRewardMutation, PurchaseRewardMutationVariables>(purchaseRewardMutation);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleEditClick = useCallback(() => {
        if (!rewardId) return;
        const path = rewardEditPath.replace(':id', rewardId);
        navigate(path);
    }, [navigate, rewardId]);

    const handlePurchaseClick = useCallback(async () => {
        if (!rewardId || !reward || reward.isRewarded || isPurchasing) return;
        if (!collectedCurrency || collectedCurrency < reward.price) return;
        
        hapticFeedback.impactOccurred.ifAvailable('light');
        
        try {
            await purchaseReward({ variables: { rewardId } });
            recordEvent(AnalyticsEventType.REWARD_PURCHASED, { rewardId, price: reward.price });
        } catch (error) {
            console.error('Error purchasing reward:', error);
        }
    }, [rewardId, reward, collectedCurrency, isPurchasing, purchaseReward, recordEvent]);

    if (!reward) {
        return (
            <Page back={true}>
                <Text>Reward data not found.</Text>
            </Page>
        );
    }

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: 'Back', onClick: navigateBack, navigation: 'back' }}
                    main={{ text: 'Edit', onClick: handleEditClick }}
                />
            }
        >
            <Stack height="100%">
                <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
                    <Text size={20}>View Reward</Text>
                </Stack>
                <motion.div
                    key="view-content"
                    style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Stack paddingLeft="10px" paddingRight="10px" paddingTop="4px" gap={2}>
                        <Stack alignSelf="center" sx={{ py: 2 }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 2,
                                background: getRewardColor(reward.rarity),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CommonIcon
                                    icon={reward.icon!}
                                    type="filled"
                                    size={48}
                                    color="var(--tg-theme-text-color)"
                                />
                            </Box>
                        </Stack>
                        
                        <Text style={{ fontSize: '20px', lineHeight: '20px', padding: '5px 0' }}>{reward.name}</Text>
                        <Text style={{ color: 'var(--tgui--hint_color)', paddingBottom: '5px' }}>{reward.description || 'No description'}</Text>
                        
                        <Stack direction="row" justifyContent="space-between" sx={{ py: '5px' }}>
                            <Stack direction="row" alignItems="center" columnGap={1}>
                                <CommonIcon icon="star" type="filled" color="var(--tg-theme-text-color)" size={20} />
                                <Text>{reward.rarity.toString()}</Text>
                            </Stack>
                            <Stack direction="row" alignItems="center" columnGap={1}>
                                <CommonIcon icon="paid" type="filled" color="var(--tg-theme-button-color)" size={20} />
                                <Text weight="2">{reward.price}</Text>
                            </Stack>
                        </Stack>

                        {!reward.isRewarded && (
                            <Stack alignSelf="center" sx={{ py: '5px' }}>
                                <MainActionButton
                                    text={`Purchase for ${reward.price}`}
                                    onClick={handlePurchaseClick}
                                    loading={isPurchasing}
                                    enabled={!!collectedCurrency && collectedCurrency >= reward.price}
                                />
                            </Stack>
                        )}

                        {reward.isRewarded && reward.rewardedOn && (
                            <Stack sx={{ py: 1 }}>
                                <Chip
                                    mode="outline"
                                    style={{ 
                                        boxShadow: '0 0 0 2px var(--tgui--button_color)',
                                        alignSelf: 'center'
                                    }}
                                    before={<CommonIcon icon="check_circle" type="filled" color="var(--tg-theme-button-color)" size={20} />}
                                >
                                    <Text>Purchased on {toDueDate(dayjs(reward.rewardedOn))}</Text>
                                </Chip>
                            </Stack>
                        )}
                    </Stack>
                </motion.div>
            </Stack>
        </Page>
    );
};
